/*
 *  @providesModule MatchSelectCellStyle
 */
import {SCALING_CELL_HEIGHT} from 'ChatConstants';
import ClientPropertyConstants from 'ClientPropertyConstants';
import Sizes from 'Sizes';
import SkillzStyle from 'SkillzStyle';
import {Platform, StyleSheet} from 'react-native';

const {
  btnTextColorOne,
  featuredMatchBarColor,
  fontBold,
  fontExtraBold,
  fontBoldItalic,
  fontPrimary,
  fontRegular,
  fontSecondary,
  fontSizeH2,
  fontSizeH3,
  fontSizeH4,
  fontSizeH5,
  fontSizeMedium,
  fontSizeVeryLarge,
  playButtonGradient,
  textColorMatchSelectPrize,
  textColorSecondary,
  viewBoxShadowPrimary,
  viewCornerRadiusTertiary,
  viewDividerColorTwo,
  viewPrimaryColor,
  viewSecondaryColor,
  text12,
  textCenter,
} = SkillzStyle.fullStyles;

const isTournamentTemplateUIV2 = ClientPropertyConstants.getTournamentTemplateUIV2Enabled();

const PLAY_BUTTON_SIZES = Sizes.dynamic({
  phone: isTournamentTemplateUIV2 ? 40 : 45,
  tablet: 68,
});

export const PRIZE_BACKGROUND_WIDTH = Sizes.dynamic({
  sm: isTournamentTemplateUIV2 ? 78 : 80,
  lg: isTournamentTemplateUIV2 ? 81.74 : 80,
  xxlg: isTournamentTemplateUIV2 ? 136 : 140,
});

export const PRIZE_BACKGROUND_HEIGHT = Sizes.dynamic({
  sm: isTournamentTemplateUIV2 ? 92 : 80,
  lg: isTournamentTemplateUIV2 ? 96.42 : 80,
  xxlg: 125,
});

const PRIZE_IMAGE_HEIGHT = Sizes.dynamic({
  sm: isTournamentTemplateUIV2 ? 82 : 80,
  lg: isTournamentTemplateUIV2 ? 85.94 : 80,
  xxlg: isTournamentTemplateUIV2 ? 115 : 125,
});

const SCALING_PRIZE_BACKGROUND_WIDTH = Sizes.dynamic({
  sm: isTournamentTemplateUIV2 ? 78 : SCALING_CELL_HEIGHT,
  lg: isTournamentTemplateUIV2 ? 81.74 : SCALING_CELL_HEIGHT,
  xxlg: 140,
});

const SCALING_PRIZE_BACKGROUND_HEIGHT = Sizes.dynamic({
  sm: isTournamentTemplateUIV2 ? 92 : SCALING_CELL_HEIGHT,
  lg: isTournamentTemplateUIV2 ? 96.42 : SCALING_CELL_HEIGHT,
  xxlg: 125,
});

export const BASE_FONT = Sizes.dynamic({
  sm: Platform.OS === 'ios' ? 43 : 36,
  lg: 45,
  xxlg: 60,
});

export const PRIZE_POOL_FONT = Sizes.dynamic({
  sm: 12,
  lg: 12.58,
  xxlg: 20,
});

export const dynamicFontSize = (charLength: number) => {
  return charLength > 4
    ? BASE_FONT * (Sizes.isTablet() ? 0.8 : 0.62)
    : charLength > 3
      ? BASE_FONT * 0.8
      : BASE_FONT;
};

export default StyleSheet.create({
  matchContentContainer: {
    backgroundColor: isTournamentTemplateUIV2 ? 'transparent' : viewSecondaryColor.backgroundColor,
    justifyContent: 'space-between',
    flexDirection: 'row',
    left: 0,
    right: 0,
    borderRadius: isTournamentTemplateUIV2 ? 12 : undefined,
    borderWidth: isTournamentTemplateUIV2 ? 1 : undefined,
    borderColor: isTournamentTemplateUIV2 ? '#E0E0E0' : undefined,
  },

  roundCorners: {
    borderRadius: isTournamentTemplateUIV2 ? 12 : 4,
    overflow: 'hidden',
  },

  roundBottomCorners: {
    borderBottomLeftRadius: 4,
    borderBottomRightRadius: 4,
    overflow: 'hidden',
  },

  matchRowsContainer: {
    flexDirection: 'column',
    flex: 1,
    backgroundColor: isTournamentTemplateUIV2 ? 'transparent' : viewSecondaryColor.backgroundColor,
  },

  matchFirstRowContainer: {
    flexDirection: 'row',
    height: Sizes.dynamic({
      sm: 60,
      xxlg: 83,
    }),
    paddingLeft: Sizes.dynamic({
      sm: 0,
      xxlg: isTournamentTemplateUIV2 ? 0 : 10,
    }),
    backgroundColor: isTournamentTemplateUIV2 ? 'transparent' : viewSecondaryColor.backgroundColor,
  },

  matchSecondRowContainer: {
    flexDirection: 'row',
    height: Sizes.dynamic({
      sm: 19,
      xxlg: 41,
    }),
    flexGrow: 1,
    justifyContent: 'flex-end',
  },
  liveEventDetailsContainer: {
    flexDirection: 'row',
    flex: 1,
  },
  createLiveEventRow: {
    flexDirection: 'row',
    height: Sizes.dynamic({
      sm: 80,
      xxlg: 103,
    }),
    paddingLeft: Sizes.dynamic({
      sm: 0,
      xxlg: 10,
    }),
    backgroundColor: isTournamentTemplateUIV2 ? 'transparent' : viewSecondaryColor.backgroundColor,
    marginVertical: isTournamentTemplateUIV2 ? 6 : 0,
    flex: 1,
  },

  matchDescriptionBlock: {
    flexDirection: 'column',
    justifyContent: 'center',
    flex: 1,
    marginRight: 1,
    padding: 0,
    margin: 0,
  },

  text: {
    paddingHorizontal: 8,
    paddingVertical: 2,
  },

  flowRight: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'stretch',
    backgroundColor: isTournamentTemplateUIV2 ? 'transparent' : viewSecondaryColor.backgroundColor,
    borderTopLeftRadius: isTournamentTemplateUIV2 ? 12 : undefined,
    borderBottomLeftRadius: isTournamentTemplateUIV2 ? 12 : undefined,
    paddingHorizontal: isTournamentTemplateUIV2 ? 5 : undefined,
  },

  prizeCellPadding: {
    paddingHorizontal: 5,
  },

  prizeText: {
    ...fontSecondary,
    fontSize: Sizes.dynamic({
      // if you update these, please update the height and width in syncTemplateIcon too
      sm: fontSizeMedium.fontSize,
      xxlg: fontSizeVeryLarge.fontSize,
    }),
    backgroundColor: 'transparent',
    ...textColorMatchSelectPrize,
    textAlign: 'center',
    alignSelf: 'center',
    ...Platform.select({
      ios: {
        // if you update this, please update prizeTextShadow too
        shadowColor: '#000000',
        shadowOpacity: 0.8,
        shadowRadius: 0,

        shadowOffset: {
          height: 2,
          width: 0,
        },
      },
    }),
  },

  prizePoolText: {
    ...fontSecondary,
    fontSize: 12,
    textAlign: 'center',
    ...textColorMatchSelectPrize,
    alignSelf: 'center',
    textShadowColor: '#494949',
    textShadowOffset: {width: 2, height: 2},
    textShadowRadius: 2,
  },

  prizeTextShadow: {
    ...Platform.select({
      ios: {
        shadowColor: '#000000',
        shadowOpacity: 0.6,
        shadowRadius: 0,

        shadowOffset: {
          height: 2,
          width: 0,
        },
      },
    }),
  },

  syncTemplateIcon: {
    ...Sizes.dynamic({
      sm: {
        height: fontSizeMedium.fontSize,
        width: fontSizeMedium.fontSize,
      },
      xxlg: {
        height: fontSizeVeryLarge.fontSize,
        width: fontSizeVeryLarge.fontSize,
      },
    }),
  },

  zCoin: {
    ...fontSecondary,
    ...fontSizeMedium,
    ...textColorMatchSelectPrize,
    shadowColor: '#000000',
    shadowOpacity: 0.8,
    shadowRadius: 1,
    shadowOffset: {
      height: 2,
      width: 0,
    },
  },

  prizeContainer: {
    backgroundColor: isTournamentTemplateUIV2 ? 'transparent' : viewSecondaryColor.backgroundColor,
    width: PRIZE_BACKGROUND_WIDTH,
    height: PRIZE_BACKGROUND_HEIGHT,
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    paddingHorizontal: Sizes.dynamic({
      sm: 0,
      xxlg: 3,
    }),
    borderTopLeftRadius: isTournamentTemplateUIV2 ? 6 : 0,
    borderBottomLeftRadius: isTournamentTemplateUIV2 ? 6 : 0,
  },

  prizeImage: {
    position: 'absolute',
    width: PRIZE_BACKGROUND_WIDTH,
    height: PRIZE_IMAGE_HEIGHT,
    borderTopLeftRadius: isTournamentTemplateUIV2 ? 6 : 0,
    borderBottomLeftRadius: isTournamentTemplateUIV2 ? 6 : 0,
  },

  prizeTextContainer: {
    justifyContent: 'center',
    alignItems: 'stretch',
    backgroundColor: 'transparent',
    flexDirection: 'row',
    flex: 1,
  },

  matchTitleText: {
    paddingHorizontal: 6,
    marginTop: 0,
    marginBottom: Sizes.dynamic({
      sm: 0,
      xxlg: 4,
    }),
    ...fontSecondary,
    fontSize: Sizes.dynamic({
      sm: fontSizeH3.fontSize,
      xxlg: fontSizeH2.fontSize,
    }),
    ...textColorSecondary,
  },

  matchTitleTextV2: {
    paddingHorizontal: 0,
    marginTop: Sizes.dynamic({
      sm: 10,
      lg: 16,
      xxlg: 4,
    }),
    marginBottom: Sizes.dynamic({
      sm: 0,
      xxlg: 4,
    }),
    ...fontExtraBold,
    fontSize: Sizes.dynamic({
      sm: 16,
      xxlg: fontSizeH2.fontSize,
    }),
    ...textColorSecondary,
    lineHeight: Sizes.dynamic({sm: 18, xxlg: 24}),
    letterSpacing: -0.48,
    backgroundColor: 'transparent',
  },

  matchTitleTextContainerInstaMatch: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: isTournamentTemplateUIV2 ? 0 : 6,
  },

  matchDescriptionText: {
    paddingHorizontal: 6,
    paddingRight: 4,
    marginBottom: 4,
    alignSelf: 'stretch',
    ...fontPrimary,
    fontSize: Sizes.dynamic({
      sm: fontSizeH5.fontSize,
      xxlg: fontSizeH3.fontSize,
    }),
    ...textColorSecondary,
  },

  matchDescriptionTextV2: {
    paddingHorizontal: 0,
    paddingRight: 4,
    marginBottom: 4,
    alignSelf: 'stretch',
    ...fontRegular,
    fontSize: Sizes.dynamic({
      sm: 13,
      xxlg: fontSizeH3.fontSize,
    }),
    ...textColorSecondary,
    lineHeight: Sizes.dynamic({sm: 17, xxlg: 20}),
    letterSpacing: -0.13,
  },
  matchDescriptionTextPrefix: {
    color: '#000000',
    flexShrink: 0,
    paddingRight: 0,
  },
  matchDescriptionTextTimePart: {
    color: '#F21C1C',
    flexShrink: 0,
    ...SkillzStyle.fullStyles.fontBold,
  },
  entryFeeText: {
    flex: 1,
    paddingHorizontal: 5,
    textAlign: 'center',
    ...fontSecondary,
    fontSize: Sizes.dynamic({
      sm: fontSizeH5.fontSize,
      xxlg: fontSizeH3.fontSize,
    }),
    ...textColorSecondary,
  },

  entryFeeTextInstaMatch: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    ...fontSecondary,
    fontSize: Sizes.dynamic({
      sm: fontSizeH5.fontSize,
      xxlg: fontSizeH3.fontSize,
    }),
    ...textColorSecondary,
  },

  entryTextInstaMatch: {
    paddingRight: 2,
  },

  zEntryFeeText: {
    textAlign: 'center',
    ...fontSecondary,
    fontSize: Sizes.dynamic({
      sm: fontSizeH5.fontSize,
      xxlg: fontSizeH3.fontSize,
    }),
    ...textColorSecondary,
  },

  matchSubtitleText: {
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    textAlignVertical: 'center',
    ...fontSecondary,
    fontSize: Sizes.dynamic({
      sm: isTournamentTemplateUIV2 ? 13 : fontSizeH5.fontSize,
      lg: isTournamentTemplateUIV2 ? 13.624 : fontSizeH5.fontSize,
      xxlg: fontSizeH3.fontSize,
    }),
    ...textColorSecondary,
  },

  prizesTextView: {
    flexDirection: 'row',
    alignItems: 'center',
  },

  dynamicFontSize: {
    ...Sizes.dynamic({
      sm: {
        fontSize: isTournamentTemplateUIV2 ? 13 : fontSizeH5.fontSize - 2,
      },
      lg: {
        fontSize: isTournamentTemplateUIV2 ? 13.624 : fontSizeH5.fontSize,
      },
      xxlg: {
        fontSize: isTournamentTemplateUIV2 ? fontSizeH3.fontSize : fontSizeH4.fontSize,
      },
    }),
  },

  smallAndroidPortraitTabletFontSize: {
    fontSize: isTournamentTemplateUIV2 ? 13 : fontSizeH5.fontSize - 2,
  },

  matchPlayersText: {
    textAlign: 'center',
    textAlignVertical: 'center',
    ...fontSecondary,
    ...textColorSecondary,
    fontSize: Sizes.dynamic({
      sm: fontSizeH5.fontSize - 1,
      lg: fontSizeH5.fontSize,
      xxlg: fontSizeH3.fontSize,
    }),
    paddingHorizontal: isTournamentTemplateUIV2 ? 1 : 2,
  },

  numPlayerText: {
    flex: 0.5,
    flexDirection: 'column',
    justifyContent: 'center',
    backgroundColor: isTournamentTemplateUIV2 ? 'transparent' : viewSecondaryColor.backgroundColor,
  },

  numPlayerTextInstaMatch: {
    flex: 0,
  },

  flowColumn: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    alignSelf: 'center',
  },

  endCap: {
    flexDirection: 'column',
    alignItems: 'flex-end',
    alignSelf: 'center',
    ...Platform.select({
      ios: {
        marginRight: Sizes.dynamic({
          sm: 9,
        }),
      },
      android: {
        marginRight: Sizes.dynamic({
          sm: 10,
          xxlg: 28,
        }),
      },
    }),
  },

  endCapInstaMatch: {
    marginRight: 0,
  },

  playButton: {
    alignItems: 'center',
    alignSelf: 'center',
    justifyContent: 'center',
    height: PLAY_BUTTON_SIZES,
    width: PLAY_BUTTON_SIZES,
  },

  playButtonAnimation: {
    width: PLAY_BUTTON_SIZES,
    height: PLAY_BUTTON_SIZES,
    borderRadius: PLAY_BUTTON_SIZES / 2,
    position: 'absolute',
  },

  betweenCellSeperator: {
    height: isTournamentTemplateUIV2 ? 0 : 1,
  },

  featuredMatchBar: {
    ...featuredMatchBarColor,
    justifyContent: 'center',
    height: 30,
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
    // 🔥 FIX: Ensure highlight text appears above tournament border highlight
    zIndex: 1000,
    elevation: 1001, // Android
    position: 'relative',
  },

  featuredMatchBarText: {
    padding: 4,
    marginHorizontal: 10,
    ...btnTextColorOne,
    ...fontBoldItalic,
    ...fontSizeH4,
  },

  limitedUseBannerContainer: {
    height: 9,
    alignItems: 'center',
    zIndex: 1,
  },

  limitedUseBannerContainerV2: {
    top: -9,
    zIndex: 1000,
    position: 'relative',
    backgroundColor: 'red',
  },

  limitedUseBanner: {
    position: 'absolute',
    height: 18,
    width: '63%',
  },

  limitedUseBannerV2: {
    position: 'absolute',
    height: 18,
    marginLeft: PRIZE_BACKGROUND_WIDTH + 12, //Plus the horizontal padding of 12 from the prize background
    borderRadius: 4,
    paddingHorizontal: 4,
  },

  limitedUseBannerText: {
    ...btnTextColorOne,
    ...fontBold,
    ...fontSizeH5,
    position: 'absolute',
  },

  limitedUseBannerTextV2: {
    ...btnTextColorOne,
    ...fontSecondary,
    fontSize: 13,
  },

  limitedUseBannerV3: {
    position: 'absolute',
    height: 18,
    marginLeft: PRIZE_BACKGROUND_WIDTH + 12, //Plus the horizontal padding of 12 from the prize background
    borderRadius: 4,
    paddingHorizontal: 4,
  },

  limitedUseBannerTextV3: {
    ...textColorSecondary,
    ...fontExtraBold,
    ...text12,
    textTransform: 'uppercase',
  },

  shadowLineView: {
    flexDirection: 'row',
    height: 1,
    backgroundColor: viewDividerColorTwo.backgroundColor,
    shadowColor: viewBoxShadowPrimary.shadowColor,
    shadowOpacity: 0.2,
    shadowRadius: 1,
    shadowOffset: {
      height: 0.5,
      width: 0,
    },
  },

  matchDetailsView: {
    flexDirection: 'row',
    justifyContent: 'center',
    flex: 1,
    alignItems: 'center',
    ...viewSecondaryColor,
  },

  matchDetailsViewInstaMatch: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    flex: 3,
    alignItems: 'center',
    ...viewSecondaryColor,
    top: 4,
  },

  originalPriceGradientInstaMatch: {
    paddingLeft: 3,
    paddingRight: 2,
    paddingVertical: 1,
    borderTopLeftRadius: 5,
    borderBottomLeftRadius: 5,
  },

  discountedPriceGradientInstaMatch: {
    paddingLeft: 2,
    paddingRight: 3,
    paddingVertical: 1,
    borderTopRightRadius: 5,
    borderBottomRightRadius: 5,
  },
  diagonalCrossOutIconInstaMatch: {
    height: '100%',
    width: '100%',
    position: 'absolute',
    left: 2,
    top: 1,
    paddingVertical: 2,
  },
  slashedDiscountedFee: {
    fontSize: Sizes.dynamic({
      sm: 22,
      xxlg: 27,
    }),
    position: 'absolute',
    color: '#FF0000',
    zIndex: 1,
  },
  pipeSeparator: {
    fontSize: 19,
    lineHeight: 20,
    color: 'gray',
    marginLeft: 2,
    marginRight: 2,
  },
  discountedFee: {
    ...fontSecondary,
    fontSize: 19,
    textAlign: 'center',
    textTransform: 'uppercase',
    top: Platform.OS === 'ios' ? 4 : 0,
    overflow: 'visible',
    ...textColorSecondary,
  },
  ticketzPrizeView: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },

  playersView: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'flex-end',
    height: Sizes.dynamic({sm: 18, xxlg: 24}),
    borderRadius: 6,
    paddingHorizontal: 4,
    marginRight: 4,
  },

  prizesDetailsView: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    flex: Sizes.orientation({
      portrait: isTournamentTemplateUIV2 ? 1 : 2,
      landscape: Sizes.dynamic({
        sm: 1.1,
        md: 1,
        xxlg: 1.4,
      }),
    }),
    alignItems: 'center',
    backgroundColor: isTournamentTemplateUIV2 ? 'transparent' : viewSecondaryColor.backgroundColor,
    paddingHorizontal: isTournamentTemplateUIV2 ? 0 : 2,
    color: 'white',
  },

  prizesDetailsViewInstaMatchDiscount: {
    flex: isTournamentTemplateUIV2 ? 2.1 : 5.25,
    top: 4,
  },

  prizesDetailsViewInstaMatchNoDiscount: {
    flex: 3.1,
  },

  tournamentCreatorPrizesDetailsView: {
    flex: Sizes.orientation({
      portrait: 2.1,
      landscape: 2,
    }),
  },

  upcomingEventOverlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: Sizes.dynamic({
      sm: 20,
      xxlg: 40,
    }),
    overflow: 'visible',
  },

  upcomingEventButton: {
    height: '100%',
    alignSelf: 'stretch',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },

  upcomingTextContainer: {
    justifyContent: 'center',
    flexDirection: 'column',
    alignSelf: 'stretch',
    alignItems: 'center',
    padding: 6,
    overflow: 'visible',
  },

  beginsInText: {
    ...btnTextColorOne,
    ...fontPrimary,
    ...fontSizeH5,
  },

  beginsInValue: {
    ...btnTextColorOne,
    ...fontSecondary,
    ...fontSizeH4,
  },

  playButtonOverlayText: {
    fontSize: 11,
    ...fontSecondary,
    color: SkillzStyle.convertToOpacity(playButtonGradient[0], 0.85), // eslint-disable-line no-magic-numbers
    textAlign: 'center',
  },

  playButtonOverlayContainer: {
    position: 'absolute',
    flex: 1,
    justifyContent: 'center',
    backgroundColor: '#fff',
    paddingHorizontal: 4,
    paddingVertical: 2,
    zIndex: 1,
    ...viewBoxShadowPrimary,
    ...viewCornerRadiusTertiary,
  },

  ticketzImage: {
    ...Sizes.dynamic({
      sm: {
        width: isTournamentTemplateUIV2 ? 12 : 10,
        height: isTournamentTemplateUIV2 ? 12 : 10,
      },
      lg: {
        width: isTournamentTemplateUIV2 ? 12.576 : 10,
        height: isTournamentTemplateUIV2 ? 12.576 : 10,
      },
      xxlg: {
        width: isTournamentTemplateUIV2 ? 20 : 16,
        height: isTournamentTemplateUIV2 ? 20 : 16,
      },
    }),
    marginRight: isTournamentTemplateUIV2 ? 1 : 0.5,
  },

  playerIconImage: {
    ...Sizes.dynamic({
      sm: {
        width: isTournamentTemplateUIV2 ? 15 : 10,
        height: isTournamentTemplateUIV2 ? 15 : 10,
      },
      xxlg: {
        width: isTournamentTemplateUIV2 ? 20 : 16,
        height: isTournamentTemplateUIV2 ? 20 : 16,
      },
    }),
    paddingHorizontal: isTournamentTemplateUIV2 ? 0 : 2,
  },

  medalImage: {
    ...Sizes.dynamic({
      sm: {
        width: isTournamentTemplateUIV2
          ? 12
          : ClientPropertyConstants.getLeaguesV3Enabled()
            ? 10
            : 6,
        height: isTournamentTemplateUIV2 ? 12.576 : 10,
      },
      lg: {
        width: isTournamentTemplateUIV2
          ? 12.576
          : ClientPropertyConstants.getLeaguesV3Enabled()
            ? 12
            : 8,
        height: isTournamentTemplateUIV2 ? 12.576 : 12,
      },
      xxlg: {
        width: ClientPropertyConstants.getLeaguesV3Enabled() ? 14 : 9,
        height: 14,
      },
    }),
    marginRight: 0.5,
  },

  customPrizeIconImage: {
    ...Sizes.dynamic({
      sm: {
        width: 10,
        height: 8,
      },
      lg: {
        width: 12,
        height: 10,
      },
      xxlg: {
        width: 14,
        height: 12,
      },
    }),
  },

  zIconImage: {
    ...Sizes.dynamic({
      sm: {
        width: 10,
        height: 10,
      },
      lg: {
        width: 12,
        height: 12,
      },
      xxlg: {
        width: 14,
        height: 14,
      },
    }),
    tintColor: textColorSecondary.color,
  },

  zIconImageEntryView: {
    marginLeft: 2,
  },

  syncSearchingFlex: {
    flex: Sizes.orientation({
      portrait: 2.1,
      landscape: Sizes.dynamic({
        sm: 2,
        xxlg: 1.4,
      }),
    }),
  },

  friendsTournamentAvatar: {
    ...Sizes.select({
      phone: {
        width: 44,
        height: 44,
      },
      tablet: {
        width: 62,
        height: 62,
      },
    }),
    alignSelf: 'center',
    marginLeft: 8,
    marginRight: 2,
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 2,
    backgroundColor: SkillzStyle.convertToOpacity(textColorSecondary.color, 0.83), // eslint-disable-line no-magic-numbers
  },

  friendsTournamentPlaceholderAvatar: {
    ...Sizes.select({
      phone: {
        width: 44,
        height: 44,
      },
      tablet: {
        width: 64,
        height: 64,
      },
    }),
    marginLeft: 8,
    marginRight: 0,
    alignSelf: 'center',
  },

  ticketzImageLarge: {
    width: PRIZE_BACKGROUND_WIDTH * 0.9,
    height: PRIZE_BACKGROUND_HEIGHT * 0.9,
    position: 'absolute',
    flex: 1,
    backgroundColor: 'transparent',
    alignSelf: 'center',
  },

  ticketzImageLargeForSync: {
    position: 'absolute',
    backgroundColor: 'transparent',
    top: -12,
    bottom: -12,
    left: -12,
    right: -12,
  },

  //scaling variables - TODO: determine how to get height from cell view itself
  scalingFriendsTournamentAvatar: {
    alignSelf: 'center',
    height: SCALING_CELL_HEIGHT * 0.5, // eslint-disable-line no-magic-numbers
    width: SCALING_CELL_HEIGHT * 0.5, // eslint-disable-line no-magic-numbers
    marginLeft: 10,
    marginRight: 4,
    borderRadius: 2,
    borderWidth: StyleSheet.hairlineWidth,
    backgroundColor: SkillzStyle.convertToOpacity(textColorSecondary.color, 0.83), // eslint-disable-line no-magic-numbers
  },

  scalingMatchRowsContainer: {
    alignSelf: 'stretch',
    flex: 1,
  },

  scalingMatchContentContainer: {
    backgroundColor: isTournamentTemplateUIV2 ? 'transparent' : viewSecondaryColor.backgroundColor,
    justifyContent: 'space-between',
    flexDirection: 'row',
    left: 0,
    right: 0,
    borderRadius: isTournamentTemplateUIV2 ? 12 : undefined,
    borderWidth: isTournamentTemplateUIV2 ? 1 : undefined,
    borderColor: isTournamentTemplateUIV2 ? '#E0E0E0' : undefined,
  },

  scalingMatchFirstRowContainer: {
    flexDirection: 'row',
    height: Sizes.dynamic({
      sm: 60,
      xxlg: 83,
    }),
    paddingLeft: Sizes.dynamic({
      sm: 0,
      xxlg: 10,
    }),
    justifyContent: 'center',

    backgroundColor: isTournamentTemplateUIV2 ? 'transparent' : viewSecondaryColor.backgroundColor,
  },

  scalingMatchSecondRowContainer: {
    flexDirection: 'row',
    flex: 0.3,
    backgroundColor: Sizes.dynamic({
      sm: viewSecondaryColor.backgroundColor,
      xxlg: SkillzStyle.convertToOpacity(viewPrimaryColor.backgroundColor, 0.2), // eslint-disable-line no-magic-numbers
    }),
  },

  scalingPlayButton: {
    alignItems: 'center',
    alignSelf: 'center',
    justifyContent: 'center',
    height: PLAY_BUTTON_SIZES,
    width: PLAY_BUTTON_SIZES,
  },

  scalingPrizeContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    width: SCALING_PRIZE_BACKGROUND_WIDTH,
    height: SCALING_PRIZE_BACKGROUND_HEIGHT,
    paddingHorizontal: Sizes.dynamic({
      sm: Platform.select({
        ios: 0,
        android: 4,
      }),
      xxlg: 8,
    }),
    borderTopLeftRadius: isTournamentTemplateUIV2 ? 6 : 0,
    borderBottomLeftRadius: isTournamentTemplateUIV2 ? 6 : 0,
  },

  scalingPrizesDetailsView: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    flex: Sizes.select({
      portraitTablet: 2.3,
      other: 2,
    }),
  },

  scalingPrizeImage: {
    position: 'absolute',
    width: PRIZE_BACKGROUND_WIDTH,
    height: PRIZE_IMAGE_HEIGHT,
    borderTopLeftRadius: isTournamentTemplateUIV2 ? 6 : 0,
    borderBottomLeftRadius: isTournamentTemplateUIV2 ? 6 : 0,
  },

  prizesPlusText: {
    paddingHorizontal: 1,
    alignSelf: 'center',
  },

  verticalViewSeperator: {
    width: StyleSheet.hairlineWidth,
    backgroundColor: viewDividerColorTwo.backgroundColor,
  },

  viewSeperator: {
    width: 1,
    backgroundColor: '#E0E0E0',
  },

  verticalViewSeperatorInstaMatch: {
    height: '90%',
    marginLeft: 2,
  },

  entryFeeTextV2: {
    ...fontSecondary,
    letterSpacing: -0.39,
    fontSize: Sizes.dynamic({
      sm: 14,
      xxlg: fontSizeH3.fontSize,
    }),
    textAlign: 'center',
    color: '#5D5D5D',
  },

  entryFeeCurrencyImage: {
    width: Sizes.dynamic({
      sm: 16,
      xxlg: 20,
    }),
    height: Sizes.dynamic({sm: 16, xxlg: 20}),
    marginRight: 1,
    marginTop: 2,
  },
  currencyItemContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    top: -4,
  },
  discountBgImage: {
    position: 'absolute',
    top: -6.5,
    right: Platform.OS === 'web' ? 92 : '18%',
    height: 47,
    width: 63,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 99,
  },
  entryFeePrizeText: {
    ...fontExtraBold,
    fontSize: Sizes.dynamic({
      sm: fontSizeH3.fontSize,
      xxlg: fontSizeH2.fontSize,
    }),
    color: '#333333',
  },

  entryFeePrizeDiscountedTextV2: {
    ...fontExtraBold,
    fontSize: 13,
    color: '#333333',
    lineHeight: 19.663,
  },

  entryFeePrizeTextV2: {
    ...fontSecondary,
    fontSize: Sizes.dynamic({
      sm: 19,
      xxlg: 24,
    }),
    color: '#333333',
    marginLeft: 2,
  },
  entryFeePrizeBeforeDiscount: {
    ...fontSecondary,
    fontSize: Sizes.dynamic({
      sm: 19,
      xxlg: 24,
    }),
    marginLeft: 2,
    position: 'relative',
    ...textColorSecondary,
  },

  playSectionContainer: {
    flex: 1,
    alignItems: 'center',
    backgroundColor: 'transparent',
  },
  playSection: {
    justifyContent: 'center',
    marginTop: Sizes.dynamic({sm: 11, lg: 13, xxlg: 20}),
    height: Sizes.dynamic({sm: 34, lg: 35.6, xxlg: 46}),
    marginBottom: Sizes.dynamic({sm: 3.51, lg: 4, xxlg: 8}),
    width: '100%',
  },
  playSectionCentered: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    height: 34,
  },

  playButtonV2: {
    alignItems: 'center',
    justifyContent: 'center',
    height: Sizes.dynamic({
      sm: 40,
      xxlg: 46,
    }),
    width: Sizes.dynamic({
      sm: 82,
      xxlg: 110,
    }),
    borderRadius: 12,
  },

  playButtonCenteredV2: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    height: Sizes.dynamic({
      sm: 40,
      xxlg: 46,
    }),
    width: Sizes.dynamic({
      sm: 82,
      xxlg: 110,
    }),
    borderRadius: 12,
  },

  playButtonInnerShadowGradient: {
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    height: Sizes.dynamic({
      sm: 32,
      xxlg: 36,
    }),
    width: Sizes.dynamic({
      sm: 72,
      xxlg: 100,
    }),
  },

  playButtonGradientV2: {
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    height: Sizes.dynamic({
      sm: 30,
      xxlg: 42,
    }),
    width: Sizes.dynamic({
      sm: 72,
      xxlg: 100,
    }),
  },

  playButtonText: {
    ...fontExtraBold,
    fontSize: Sizes.dynamic({
      sm: fontSizeH4.fontSize,
      xxlg: fontSizeH3.fontSize,
    }),
    textAlign: 'center',
    color: '#FFFFFF',
    textShadowColor: '#00000040',
    textShadowOffset: {width: 0, height: -0.5},
    textShadowRadius: 0.3,
    letterSpacing: -0.57,
  },
  rightContainer: {
    width: Sizes.dynamic({
      sm: 90,
      lg: 94.3,
      xxlg: 120,
    }),
    borderTopRightRadius: 12,
    borderBottomRightRadius: 12,
    flexDirection: 'row',
    justifyContent: 'center',
    backgroundColor: 'transparent',
  },
  svgMainContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    bottom: -2,
  },
  svgContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },

  ticketzPrizeImage: {
    width: Sizes.dynamic({
      sm: 16,
      xxlg: 25,
    }),
    height: Sizes.dynamic({
      sm: 16,
      xxlg: 25,
    }),

    verticalAlign: 'middle',
  },

  registrationClosedContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: Sizes.dynamic({
      sm: 8,
      xxlg: 12,
    }),
    paddingVertical: Sizes.dynamic({
      sm: 12,
      xxlg: 16,
    }),
    flexWrap: 'wrap',
    maxWidth: '100%',
  },

  registrationClosedText: {
    ...fontExtraBold,
    ...textColorSecondary,
    fontSize: 14,
    marginRight: 2,
    lineHeight: 14,
    letterSpacing: -0.42,
    marginTop: 8,
    textTransform: 'capitalize',
    textAlign: 'center',
    flexWrap: 'wrap',
    flexShrink: 1,
  },

  registrationBannerText: {
    ...fontExtraBold,
    ...textCenter,
    padding: 6,
    color: '#FFFFFF',
  },
});
